/**
 * Execute plain-JavaScript code in an opaque sandboxed <iframe>.
 *
 *  • No TypeScript / JSX support.
 *  • Snippet must define a global function (e.g. `function transform` or
 *    `const clean = …`) that we can call.
 *  • iframe uses sandbox="allow-scripts" – no same-origin access.
 *
 * @param code      user snippet (string)
 * @param input     JSON-serialisable arg passed to the callable
 * @param funcName  optional symbol name (default: 'transform' or last
 *                  global function defined)
 */
export async function runJsCode<T>(
  code: string,
  input: unknown,
  funcName?: string
): Promise<T> {
  /* 1️⃣  Prepare HTML for the sandbox */
  const html = `
<!DOCTYPE html>
<script>
  function serializeError(err) {
    if (err instanceof Error) {
      return {
        name: err.name,
        message: err.message,
        stack: err.stack,
        ...Object.getOwnPropertyNames(err).reduce((acc, prop) => {
          if (!['name', 'message', 'stack'].includes(prop)) {
            acc[prop] = err[prop];
          }
          return acc;
        }, {})
      };
    }
    return { name: 'Error', message: String(err), stack: undefined };
  }

  try {
    /* --- user snippet inserted --- */
    ${code}

    /* --- pick callable --- */
    let fn =
      ${funcName ? `window[${JSON.stringify(funcName)}]` : "null"} ||
      window.transform ||
      Object.values(window).reverse().find(v => typeof v === "function");

    if (typeof fn !== "function")
      throw new Error("No callable function found in snippet");

    const result = fn(${JSON.stringify(input)});
    parent.postMessage({ res: result }, "*");
  } catch (err) {
    parent.postMessage({ err: serializeError(err) }, "*");
  }
<\/script>`;

  /* 2️⃣  Spin up sandboxed iframe (opaque origin) */
  const frame = document.createElement("iframe");
  frame.style.display = "none";
  frame.sandbox.add("allow-scripts");
  frame.srcdoc = html;
  document.body.appendChild(frame);

  /* 3️⃣  Await the result */
  return new Promise<T>((resolve, reject) => {
    const onMsg = (e: MessageEvent) => {
      if (e.source !== frame.contentWindow) return; // ignore others
      window.removeEventListener("message", onMsg);
      frame.remove();

      if (e.data.err) {
        const errorData = e.data.err;
        const error = new Error(errorData.message);
        error.name = errorData.name;
        if (errorData.stack) error.stack = errorData.stack;
        Object.assign(error, errorData);
        reject(error);
      } else {
        resolve(e.data.res);
      }
    };
    window.addEventListener("message", onMsg);
  });
}


let py: any;                     // memoised Pyodide instance
let loading: Promise<void> | null = null;

/* Update these two lines together when Pyodide releases a new version */
const PYODIDE_VERSION = "0.27.7";
const PYODIDE_BASE = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`;

/* Helper: load pyodide.js exactly once, initialise Pyodide exactly once */
async function ensurePyodide() {
  if (py) return;                // already initialised

  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `${PYODIDE_BASE}pyodide.js`;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Failed to load pyodide.js"));
      document.head.appendChild(script);
    }).then(async () => {
      // @ts-ignore – the script adds loadPyodide to window
      py = await (window as any).loadPyodide({ indexURL: PYODIDE_BASE });
    });
  }
  return loading;
}

/**
 * Run a Python snippet that defines or returns a callable.
 *
 * @param code       Python code
 * @param input      JSON-serialisable argument
 * @param funcName   optional callable name (auto-detect if omitted)
 */
export async function runPyCode<T>(
  code: string,
  input: unknown,
  funcName?: string
): Promise<T> {
  await ensurePyodide();         // loads script & initialises once!

  try {
    /* 1️⃣  Evaluate the user's code so symbols enter globals() */
    await py.runPythonAsync(code);

    /* 2️⃣  Pick the function to call */
    if (!funcName) {
      funcName = py.runPython(`
import types
next(name for name, val in reversed(list(globals().items()))
     if isinstance(val, types.FunctionType) and not name.startswith('_'))
`);
    }

    /* 3️⃣  Call the function and JSON-serialise the result */
    const resultJSON: string = await py.runPythonAsync(`
import json
import traceback
try:
    _input = json.loads(${JSON.stringify(JSON.stringify(input))})
    out    = globals()[${JSON.stringify(funcName)}](_input)
    json.dumps({"success": True, "result": out})
except Exception as e:
    json.dumps({
        "success": False,
        "error": {
            "name": type(e).__name__,
            "message": str(e),
            "traceback": traceback.format_exc(),
            "args": e.args
        }
    })
`);

    const result = JSON.parse(resultJSON);

    if (!result.success) {
      const errorData = result.error;
      const error = new Error(errorData.message);
      error.name = `Python${errorData.name}`;
      error.stack = errorData.traceback;
      (error as any).args = errorData.args;
      throw error;
    }

    return result.result;

  } catch (error) {
    // Handle Pyodide-level errors (syntax errors, etc.)
    if (error instanceof Error && !error.name.startsWith('Python')) {
      error.name = `PyodideError: ${error.name}`;
    }
    throw error;
  }
}
